import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface Team {
  id: string;
  name: string;
  teamNumber: string;
}

const CreateLeague: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Only admins can create leagues
    if (user && !isAdminOrSuperAdmin(user.profileType)) {
      navigate('/leagues');
    }
  }, [user, navigate]);
  const [formData, setFormData] = useState({
    name: '',
    registrationFee: '',
    maxPlayers: '',
    minPlayers: '',
    matchDuration: '',
    teamIds: [] as string[]
  });
  const [teams, setTeams] = useState<Team[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await api.get('/teams', { params: { page: '1', limit: '100' } });
      const teams = response.data.teams || response.data;
      setTeams(teams.filter((t: any) => t.status === 'approved'));
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleTeamToggle = (teamId: string) => {
    setFormData({
      ...formData,
      teamIds: formData.teamIds.includes(teamId)
        ? formData.teamIds.filter(id => id !== teamId)
        : [...formData.teamIds, teamId]
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const rules: Record<string, any> = {};
      if (formData.maxPlayers) rules.maxPlayers = parseInt(formData.maxPlayers);
      if (formData.minPlayers) rules.minPlayers = parseInt(formData.minPlayers);
      if (formData.matchDuration) rules.matchDuration = parseInt(formData.matchDuration);

      const response = await api.post('/leagues', {
        name: formData.name,
        registrationFee: formData.registrationFee ? parseFloat(formData.registrationFee) : undefined,
        rules: Object.keys(rules).length > 0 ? rules : undefined,
        teamIds: formData.teamIds
      });

      alert('League created successfully!');
      navigate('/leagues');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create league');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdminOrSuperAdmin(user?.profileType)) {
    return (
      <div className="container">
        <div className="card">
          <h2>Access Denied</h2>
          <p>Only administrators can create leagues.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '800px', marginTop: '50px' }}>
      <div className="card">
        <h1>Create New League</h1>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>League Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Utah Premier League"
            />
          </div>

          <div className="form-group">
            <label>Registration Fee ($)</label>
            <input
              type="number"
              name="registrationFee"
              step="0.01"
              min="0"
              value={formData.registrationFee}
              onChange={handleChange}
              placeholder="e.g., 75.00"
            />
            <small style={{ color: '#757575', fontSize: '12px' }}>Per-player fee charged when joining a team in this league</small>
          </div>

          <div style={{ marginTop: '30px', marginBottom: '20px' }}>
            <h3>League Rules (Optional)</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>Max Players per Team</label>
                <input
                  type="number"
                  name="maxPlayers"
                  value={formData.maxPlayers}
                  onChange={handleChange}
                  min="1"
                  placeholder="e.g., 20"
                />
              </div>

              <div className="form-group">
                <label>Min Players per Team</label>
                <input
                  type="number"
                  name="minPlayers"
                  value={formData.minPlayers}
                  onChange={handleChange}
                  min="1"
                  placeholder="e.g., 11"
                />
              </div>

              <div className="form-group">
                <label>Match Duration (minutes)</label>
                <input
                  type="number"
                  name="matchDuration"
                  value={formData.matchDuration}
                  onChange={handleChange}
                  min="1"
                  placeholder="e.g., 90"
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '30px', marginBottom: '20px' }}>
            <h3>Assign Teams *</h3>
            <p style={{ color: '#757575', fontSize: '14px', marginBottom: '15px' }}>
              Select teams to participate in this league
            </p>
            {teams.length === 0 ? (
              <p style={{ color: '#9E9E9E' }}>No approved teams available. Create teams first.</p>
            ) : (
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '10px' }}>
                {teams.map((team) => (
                  <label
                    key={team.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      marginBottom: '5px',
                      background: formData.teamIds.includes(team.id) ? '#E8F5E9' : 'transparent'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.teamIds.includes(team.id)}
                      onChange={() => handleTeamToggle(team.id)}
                      style={{ width: '20px', height: '20px', marginRight: '10px', cursor: 'pointer' }}
                    />
                    <div>
                      <strong>{team.name}</strong>
                      <span style={{ color: '#757575', marginLeft: '10px' }}>{team.teamNumber}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {formData.teamIds.length > 0 && (
              <p style={{ marginTop: '10px', color: '#757575', fontSize: '14px' }}>
                {formData.teamIds.length} team(s) selected
              </p>
            )}
          </div>

          {error && <div className="error">{error}</div>}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button type="submit" className="btn btn-primary" disabled={loading || formData.teamIds.length === 0}>
              {loading ? 'Creating...' : 'Create League'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/leagues')}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLeague;

